import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, BookMarked, Sparkles, ChevronLeft, ChevronRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import { api } from "@/lib/api";

const WEEKDAY_ORDER = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"];
const WEEKDAY_LABEL = {
  "domingo": "Dom", "segunda": "Seg", "terça": "Ter", "quarta": "Qua",
  "quinta": "Qui", "sexta": "Sex", "sábado": "Sáb",
};

const FUNCTION_LABEL = {
  fundamento: "Fundamento",
  desenvolvimento: "Desenvolvimento",
  aplicacao: "Aplicação",
};
const FUNCTION_ACCENT = {
  fundamento: "border-[#0B1E36] text-[#0B1E36]",
  desenvolvimento: "border-[#C5A059] text-[#C5A059]",
  aplicacao: "border-[#7B3F00] text-[#7B3F00]",
};

// dia da semana atual (0=domingo, 6=sábado) no fuso local do navegador
function todayIndex() {
  return new Date().getDay();
}

export default function Semana() {
  const [theme, setTheme] = useState(null);
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [activeIdx, setActiveIdx] = useState(todayIndex());

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setErr(null);
    api
      .get("/weekly-theme/current")
      .then(async ({ data }) => {
        if (!mounted) return;
        const t = data?.theme;
        if (!t?.theme_slug) {
          setErr("Nenhum tema semanal ativo no momento.");
          setLoading(false);
          return;
        }
        setTheme(t);
        try {
          const daysResp = await api.get(`/weekly-theme/${t.theme_slug}/days`);
          if (!mounted) return;
          const rawDays = daysResp.data?.days || [];
          const ordered = [...rawDays].sort((a, b) => (a.day_index ?? 0) - (b.day_index ?? 0));
          setDays(ordered);
        } catch (e) {
          if (!mounted) return;
          setErr("Os sete dias desta semana ainda estão sendo preparados.");
        }
      })
      .catch(() => {
        if (!mounted) return;
        setErr("Não foi possível carregar a semana.");
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });
    return () => { mounted = false; };
  }, []);

  const active = useMemo(
    () => days.find((d) => (d.day_index ?? -1) === activeIdx) || days[0] || null,
    [days, activeIdx]
  );

  const goPrev = () => setActiveIdx((i) => (i - 1 + 7) % 7);
  const goNext = () => setActiveIdx((i) => (i + 1) % 7);

  return (
    <div data-testid="semana-page" className="bg-white min-h-screen">
      <Navbar />
      <main className="pt-28 pb-24">
        <div className="max-w-3xl mx-auto px-6 md:px-8">
          <Link
            to="/"
            data-testid="semana-back-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#111827]/70 hover:text-[#C5A059] transition-colors duration-300"
          >
            <ArrowLeft size={14} strokeWidth={1.5} />
            Voltar ao início
          </Link>

          {loading && (
            <div className="mt-16 space-y-6 animate-pulse" data-testid="semana-loading">
              <div className="h-4 bg-[#F5F7FA] w-40" />
              <div className="h-12 bg-[#F5F7FA] w-3/4" />
              <div className="h-4 bg-[#F5F7FA] w-1/2" />
              <div className="h-32 bg-[#F5F7FA] w-full" />
            </div>
          )}

          {!loading && err && (
            <div className="mt-24 text-center text-[#111827]/70" data-testid="semana-error">{err}</div>
          )}

          {!loading && !err && theme && (
            <>
              {/* Hero */}
              <header className="mt-12">
                <p className="text-[0.72rem] uppercase tracking-[0.3em] text-[#C5A059]" data-testid="semana-anchor">
                  Semana Temática · {theme.anchor_category}
                </p>
                <h1
                  data-testid="semana-title"
                  className="mt-6 font-serif-display text-[2.4rem] sm:text-4xl md:text-5xl lg:text-[3.75rem] text-[#0B1E36] leading-[1.05] tracking-[-0.028em] font-medium"
                >
                  {theme.theme_name}
                </h1>
                {theme.theme_description && (
                  <p className="mt-6 text-[#111827]/75 text-base md:text-lg leading-relaxed max-w-2xl">
                    {theme.theme_description}
                  </p>
                )}
                {theme.verse_reference && theme.verse_text && (
                  <div className="mt-10 border-l-2 border-[#C5A059] pl-6 py-2" data-testid="semana-verse">
                    <p className="font-serif-display italic text-[1.2rem] md:text-[1.4rem] text-[#0B1E36]/85 leading-[1.55]">
                      &ldquo;{theme.verse_text}&rdquo;
                    </p>
                    <p className="mt-3 text-xs uppercase tracking-[0.28em] text-[#C5A059] font-medium">
                      {theme.verse_reference}
                    </p>
                  </div>
                )}
              </header>

              {/* Navegação dos 7 dias */}
              {days.length > 0 && (
                <nav
                  data-testid="semana-day-nav"
                  className="mt-16 border-t border-[#E5E7EB] pt-8"
                  aria-label="Dias da semana"
                >
                  <div className="flex items-center justify-between gap-4">
                    <button
                      data-testid="semana-prev"
                      onClick={goPrev}
                      aria-label="Dia anterior"
                      className="p-3 min-w-[44px] min-h-[44px] flex items-center justify-center text-[#0B1E36]/70 hover:text-[#C5A059] transition"
                    >
                      <ChevronLeft size={22} strokeWidth={1.5} />
                    </button>
                    <ul className="flex gap-1.5 sm:gap-2 flex-1 justify-center">
                      {WEEKDAY_ORDER.map((wd, i) => {
                        const d = days.find((x) => (x.day_index ?? -1) === i);
                        const isActive = i === activeIdx;
                        return (
                          <li key={i}>
                            <button
                              data-testid={`semana-day-tab-${i}`}
                              onClick={() => setActiveIdx(i)}
                              disabled={!d}
                              className={`px-3 sm:px-4 py-3 min-h-[44px] text-[0.7rem] sm:text-xs uppercase tracking-[0.2em] font-medium transition
                                ${isActive
                                  ? "bg-[#0B1E36] text-white"
                                  : d
                                    ? "text-[#0B1E36]/70 hover:text-[#C5A059] border border-[#E5E7EB]"
                                    : "text-[#111827]/25 border border-[#F5F7FA] cursor-not-allowed"
                                }`}
                            >
                              {WEEKDAY_LABEL[wd]}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                    <button
                      data-testid="semana-next"
                      onClick={goNext}
                      aria-label="Próximo dia"
                      className="p-3 min-w-[44px] min-h-[44px] flex items-center justify-center text-[#0B1E36]/70 hover:text-[#C5A059] transition"
                    >
                      <ChevronRight size={22} strokeWidth={1.5} />
                    </button>
                  </div>
                </nav>
              )}

              {/* Conteúdo do dia ativo */}
              {active && (
                <DayContent day={active} />
              )}

              {!active && days.length === 0 && (
                <p className="mt-24 text-center text-[#111827]/70">
                  Os sete dias desta semana ainda estão sendo preparados.
                </p>
              )}

              <div className="mt-24 flex flex-col sm:flex-row gap-4">
                <Link to="/devocional" data-testid="semana-to-devocional" className="btn-secondary-dark">
                  Devocional de hoje
                </Link>
                <Link to="/#livros" className="btn-primary-gold">
                  Aprofundar com um livro
                </Link>
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}

function DayContent({ day }) {
  const palavra = day.palavra || {};
  const conns = day.scripture_connections || [];
  return (
    <article className="mt-16" data-testid={`semana-day-content-${day.day_index}`}>
      <p className="text-[0.68rem] uppercase tracking-[0.28em] text-[#C5A059]">
        {WEEKDAY_LABEL[day.weekday] ? `Dia ${day.day_index + 1} · ${capitalize(day.weekday)}` : `Dia ${day.day_index + 1}`}
      </p>

      {day.abertura && (
        <p className="mt-6 font-serif-display text-[1.5rem] md:text-[1.85rem] text-[#0B1E36] italic leading-[1.35] tracking-[-0.014em]">
          {day.abertura}
        </p>
      )}

      {palavra.text && (
        <div className="mt-10 border-l-2 border-[#C5A059] pl-6 py-2" data-testid="semana-day-palavra">
          <p className="font-serif-display italic text-[1.2rem] md:text-[1.4rem] text-[#0B1E36]/85 leading-[1.55]">
            &ldquo;{palavra.text}&rdquo;
          </p>
          <p className="mt-3 text-xs uppercase tracking-[0.28em] text-[#C5A059] font-medium">
            {palavra.reference}
          </p>
        </div>
      )}

      {day.verdade && (
        <section className="mt-14" data-testid="semana-day-verdade">
          <div className="flex items-center gap-3">
            <Sparkles size={18} strokeWidth={1.5} className="text-[#C5A059]" />
            <h2 className="eyebrow">Verdade</h2>
          </div>
          <p className="mt-6 devotional-prose">{day.verdade}</p>
        </section>
      )}

      {day.hoje && (
        <section className="mt-14" data-testid="semana-day-hoje">
          <h2 className="eyebrow">Hoje</h2>
          <p className="mt-6 devotional-prose">{day.hoje}</p>
        </section>
      )}

      {day.oracao && (
        <section className="mt-14" data-testid="semana-day-oracao">
          <h2 className="eyebrow">Oração</h2>
          <p className="mt-6 serif-body">{day.oracao}</p>
        </section>
      )}

      {day.frase_compartilhar && (
        <section className="mt-14 py-14 border-y border-[#E5E7EB] text-center" data-testid="semana-day-frase">
          <BookMarked size={22} strokeWidth={1.5} className="text-[#C5A059] mx-auto" />
          <p className="mt-8 font-serif-display text-[1.6rem] md:text-[2rem] text-[#0B1E36] italic leading-[1.32] tracking-[-0.018em] max-w-xl mx-auto">
            &ldquo;{day.frase_compartilhar}&rdquo;
          </p>
        </section>
      )}

      {conns.length > 0 && (
        <section className="mt-14" data-testid="semana-day-connections">
          <h2 className="eyebrow">Passagens que Ecoam</h2>
          <div className="mt-8 space-y-6">
            {conns.map((c, i) => {
              const fn = (c.theological_function || "").toLowerCase();
              const accent = FUNCTION_ACCENT[fn] || "border-[#C5A059] text-[#C5A059]";
              return (
                <div
                  key={i}
                  data-testid={`semana-day-connection-${i}`}
                  className={`border-l-2 pl-5 py-3 ${accent}`}
                >
                  <p className="text-[0.68rem] uppercase tracking-[0.28em] font-medium">
                    {FUNCTION_LABEL[fn] || "Referência"}
                  </p>
                  <p className="mt-2 font-serif-display italic text-[1.05rem] md:text-[1.15rem] text-[#0B1E36]/85 leading-[1.55]">
                    &ldquo;{c.verse_text}&rdquo;
                  </p>
                  <p className="mt-3 text-xs uppercase tracking-[0.22em] text-[#0B1E36]/70">
                    {c.reference}
                  </p>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </article>
  );
}

function capitalize(s) {
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}
